import { useState, useCallback, useMemo } from 'react';
import { Ajax, useAction, cookies } from 'utils';
import { getParam } from 'utils/ebUtils';

const API_URLS = {
  INIT: (orgId, key) => (key === '' ?
    `/rest/init?orgId=${orgId}` :
    `/rest/init?orgId=${orgId}&accessKey=${key}`),
  ACCOUNT: (orgId) => `/rest/init/account?orgId=${orgId}`,
  CHANGE_LANGUAGE: (lang) => `/changeLanguage/${lang}`,
  FEATURE_FLAGS: (orgId) => `/featureFlags?orgId=${orgId}`,
  COMMS: (commsId) => `/rest/comms/${commsId}`,
  CSRF_TOKEN: (orgId) => `/rest/init/csrf?orgId=${orgId}`,
};

const ERROR_MESSAGES = {
  NO_COMMS_ID: 'No commsId',
};

export const useChangeLang = () =>
  useAction(({ lang }) => Ajax.post(API_URLS.CHANGE_LANGUAGE(lang), {}));

// create custom hook for fetching data based on the changable
// this hook will return request state, performAction function and cancel function
// request state will contain loading, status, data and error
// performAction function will call the requestFn with the params and cancelToken
const createFetchHook = (requestFn) => (params = {}) => {
  // create cancel token and cancel function
  const { cancel, cancelToken } = useMemo(() => {
    let newCancel;
    const newCancelToken = new Ajax.CancelToken((c) => {
      newCancel = c;
    });
    return { cancel: newCancel, cancelToken: newCancelToken };
  }, []);

  // create state for request and set initial state
  // which will be returned from the hook
  const [request, setRequest] = useState({
    loading: false
  });

  // create function to perform action
  // this function will call the requestFn with the params and cancelToken
  // and set the state based on the response
  const performAction = useCallback(async (actionParams, done, fail) => {
    setRequest({ loading: true });
    const response = await requestFn(params, cancelToken, actionParams);
    const { status, data: result, hasError, isCanceled } = response;


    let error;
    let data;

    // if hasError is true, set error state and call fail function
    if (hasError) {
      error = result || { isCanceled };
      if (fail) fail(result);
    } else {
      data = result;
      if (done) done(result);
    }

    // set request state based on the response from the requestFn
    setRequest({
      loading: false,
      status,
      data,
      error
    });

    return response;
  }, [params, cancelToken]);


  // return request state, performAction function and cancel function
  return [request, performAction, cancel];
};

export const useFetchBootData = createFetchHook(({ orgId, accessKey, loggedIn }, cancelToken) => {
  const accessKeyFromCookie = cookies.get("accessKey", {
    path: `/${orgId}`
  });
  if ((accessKey !== undefined && accessKey !== '') && (accessKeyFromCookie !== accessKey || loggedIn)) {
    // addToLocal('accessKey', accessKey);
    cookies.set('accessKey', accessKey, {
      path: `/${orgId}`
    });
  }
  const key = (accessKey !== undefined && accessKey !== '') ? accessKey : accessKeyFromCookie;
  const url = API_URLS.INIT(orgId, key);
  return Ajax.get(url, { cancelToken });
});

export const useCSRFData = createFetchHook(({ orgId }, cancelToken) => {
  return Ajax.get(API_URLS.CSRF_TOKEN(orgId),
    null,
    {
      cancelToken,
      ...(getParam('Authorization') ? {
        headers: { Authorization: `token ${getParam('Authorization')}` }
      } : {}),
    }
  );
});


export const useFetchAccountData = createFetchHook(({ orgId }, cancelToken) =>
  Ajax.get(API_URLS.ACCOUNT(orgId), {
    cancelToken,
    ...(getParam('Authorization') ? {
      headers: { Authorization: `token ${getParam('Authorization')}` }
    } : {})
  })
);

export const useSplitsData = createFetchHook(({ orgId }, cancelToken) =>
  Ajax.post(API_URLS.FEATURE_FLAGS(orgId), [{
    trafficType: 'organization',
    names: ['cme_mbr_delelteButton', 'ebs_trm_enableSecuredTravelEducationPortal', 'PUBLIC_PORTAL_HIDE_LOGIN_TO_MP', 'PUBLIC_PORTAL_SHOW_LOGIN_TO_UP', 'PUBLIC_PORTAL_FORCE_LINKED_ACCOUNT_TO_UP', 'PUBLIC_PORTAL_FORCE_NEW_ACCOUNT_TO_UP'],
  }], { cancelToken })
);

// create custom hook for fetching comms data
export const useFetchCommsData = createFetchHook(({ commsId }, cancelToken) => {
  if (!commsId) return Promise.reject(new Error(ERROR_MESSAGES.NO_COMMS_ID));
  return Ajax.get(API_URLS.COMMS(commsId), { cancelToken });
});
